import {
    Combobox,
    Transition,
    ComboboxInput,
    ComboboxButton,
    ComboboxOptions,
    ComboboxOption,
} from '@headlessui/react';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { ExtensionKey } from 'flow/types/EntityKeys';

import { licenseIsMobile } from 'flow/utils/flow-utils';
import { twMerge } from 'tailwind-merge';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { selectMainNumberOptions } from 'flow/selectors/profile-selectors';
import React, { Fragment, useState } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';

interface Option {
    id: string;
    name: string;
}

interface Props {
    /**
     * Names the field for assistive tech. The visible label sits outside this
     * component, so only the caller can say what this combobox is for.
     */
    ariaLabel?: string;
    caller: Option;
    extensionKey: ExtensionKey;
    onChange: (newValue: Option) => void;
}

const CallerCombobox = ({
    caller,
    extensionKey,
    onChange,
    ariaLabel,
}: Props) => {
    const { i18next } = useAppContext();
    const { extension } = useExtension(extensionKey);

    const mainNumbers = useAppSelector(selectMainNumberOptions);

    const hasMobileLicense = licenseIsMobile(
        extension.getIn(['license', 'licenseUsageType']),
    );

    const [query, setQuery] = useState('');

    const defaultOptions: Option[] = [
        { id: 'fixed', name: i18next.t('settings.landline_number') },
        ...(hasMobileLicense
            ? [{ id: 'msisdn', name: i18next.t('settings.mobile_number') }]
            : []),
        ...(!IS_UAE_HOST
            ? [{ id: 'hidden', name: i18next.t('settings.hidden_number') }]
            : []),
    ].concat(mainNumbers);

    const normalizedQuery = query.toLowerCase().replace(/\s+/g, '');
    const filteredCallIds =
        normalizedQuery === ''
            ? defaultOptions
            : defaultOptions.filter((option) =>
                  option.name
                      .toLowerCase()
                      .replace(/\s+/g, '')
                      .includes(normalizedQuery),
              );

    return (
        <Combobox value={caller} by="id" onChange={onChange}>
            <div className="relative w-full text-sm/4">
                <ComboboxInput
                    aria-label={ariaLabel}
                    className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-9 text-gray-900"
                    displayValue={(option: Option) => option?.name}
                    onChange={(event) => setQuery(event.target.value)}
                />
                <ComboboxButton className="absolute inset-y-0 end-0 flex items-center pe-2">
                    <Icon
                        className="size-5 text-gray-600"
                        iconKey="arrow_down"
                        aria-hidden="true"
                    />
                </ComboboxButton>

                {/* TODO: #vr - WEB-14419 - This should use popper to make the menu place correctly inside the modal  */}
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery('')}
                >
                    <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                        {filteredCallIds.map((option) => (
                            <ComboboxOption
                                key={option.id}
                                className={({ focus }) =>
                                    twMerge(
                                        'relative cursor-default py-2 ps-3 pe-9 select-none',
                                        focus ? 'bg-gray-200' : 'text-gray-900',
                                    )
                                }
                                value={option}
                            >
                                {({ selected }) => (
                                    <span
                                        className={twMerge(
                                            'truncate text-gray-500',
                                            selected && 'font-medium',
                                        )}
                                    >
                                        {option.name}
                                    </span>
                                )}
                            </ComboboxOption>
                        ))}
                    </ComboboxOptions>
                </Transition>
            </div>
        </Combobox>
    );
};

export default CallerCombobox;
